import { useState, useEffect, useContext } from 'react';
import CompanyManagement from '../components/settings/CompanyManagement';
import AvisManagement from '../components/settings/AvisManagement';
import { ApiContext } from '../App';
import { toast } from 'react-toastify';
import Breadcrumb from '../components/utils/Breadcrumb';
import ConfirmationDialog from '../components/utils/ConfirmationDialog';

const AdminTab = () => {
  const API = useContext(ApiContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [companies, setCompanies] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [avisToDelete, setAvisToDelete] = useState(null);
  const [showCompanyChangeDialog, setShowCompanyChangeDialog] = useState(false);
  const [pendingCompanyChange, setPendingCompanyChange] = useState(null);

  // Récupérer tous les utilisateurs au chargement
  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  // Fonction pour récupérer les utilisateurs
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/user/all');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

    // Récupérer les entreprises
  const fetchCompanies = async () => {
    try {
      const response = await API.get('/api/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Erreur lors du chargement des entreprises');
    }
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return "-";
    const company = companies.find(comp => comp._id === companyId);
    return company ? company.name : `ID: ${companyId}`
  }

  // Vérifier si la photo de profil est une URL externe (Google) ou une clé S3
  const isExternalPhotoUrl = (photoUrl) => {
    if (!photoUrl) return false;
    return photoUrl.startsWith('http://') || photoUrl.startsWith('https://');
  }; 

  // Fonction pour mettre à jour le rôle d'un utilisateur
  const handleRoleChange = async (userId, newRole) => {
    try {
      await API.put(`/api/user/${userId}/role`, { role: newRole });

      // Mettre à jour l'état local
      setUsers(users.map(user =>
        user._id === userId ? { ...user, role: newRole } : user
      ));

      toast.success(`Rôle mis à jour avec succès: ${newRole}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Erreur lors de la mise à jour du rôle');
    }
  };

  // Fonction pour ouvrir le dialog de confirmation de changement d'entreprise
  const handleCompanyChangeRequest = (userId, newCompanyId) => {
    const user = users.find(u => u._id === userId);
    const company = companies.find(c => c._id === newCompanyId);

    setPendingCompanyChange({
      userId,
      newCompanyId,
      userName: user?.name,
      companyName: company?.name
    });
    setShowCompanyChangeDialog(true);
  };

  // Fonction pour confirmer et appliquer le changement d'entreprise
  const confirmCompanyChange = async () => {
    if (!pendingCompanyChange) return;

    const { userId, newCompanyId, companyName } = pendingCompanyChange;

    setShowCompanyChangeDialog(false);

    try {
      await API.put(`/api/user/${userId}/company`, { companyId: newCompanyId });

      // Mettre à jour l'état local
      setUsers(users.map(user =>
        user._id === userId ? { ...user, companyId: newCompanyId, company: companyName } : user
      ));

      toast.success(`Entreprise mise à jour avec succès: ${companyName}`);
    } catch (error) {
      console.error('Error updating user company:', error);
      toast.error('Erreur lors de la mise à jour de l\'entreprise');
    } finally {
      setPendingCompanyChange(null);
    }
  };

  // Fonction pour mettre à jour le statut tester d'un utilisateur
  const handleTesterChange = async (userId, isTester) => {
    try {
      await API.put(`/api/user/${userId}/tester`, { tester: isTester });

      // Mettre à jour l'état local
      setUsers(users.map(user =>
        user._id === userId ? { ...user, tester: isTester } : user
      ));

      toast.success(`Statut tester mis à jour: ${isTester ? 'Activé' : 'Désactivé'}`);
    } catch (error) {
      console.error('Error updating user tester status:', error);
      toast.error('Erreur lors de la mise à jour du statut tester');
    }
  };

  // Filtrer les utilisateurs selon le terme de recherche
  const filteredUsers = searchTerm
    ? users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : users;

  const handleDelete = async () => {
    if (!avisToDelete) return;

    const avisId = avisToDelete;
    setShowDeleteDialog(false);
    setAvisToDelete(null);

    try {
      await API.delete(`/api/avis/${avisId}`);
      toast.success('Avis supprimé avec succès');
    } catch (error) {
      console.error('Error deleting avis:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 bg-gray-100 min-h-screen pt-16">
      {/* Breadcrumb */}
      <div className='pt-10'>
        <Breadcrumb items={[
          { label: 'Dashboard', href: '/app', icon: 'bi-house' },
          { label: 'Admin Panel', href: null, icon: 'bi-shield', isActive: true }
        ]} />
      </div>

      {/* Header Section */}
      <div className=" pb-6">
        <header className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <i className="bi bi-shield-check text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-2xl sm:text-xx3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                  Panneau d'Administration
                </h1>
                <p className="text-sm sm:text-base text-slate-600 mt-1">
                  Gérez les droits et permissions de vos utilisateurs
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-2 rounded-full border border-green-200">
              <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-green-700">Système actif</span>
            </div>
          </div>
        </header>
      </div>

      {/* Content Section */}
      <div className=" pb-10">
        <div className="grid grid-cols-1 gap-6 sm:gap-8">
          {/* Section de gestion des entreprises */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <i className="bi bi-building text-white text-sm"></i>
                </div>
                <h2 className="text-lg font-semibold text-slate-800">Gestion des Entreprises</h2>
              </div>
            </div>
            <div className="p-6">
              <CompanyManagement />
            </div>
          </div>
          
          {/* Section de gestion des rôles d'utilisateurs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <i className="bi bi-people text-white text-sm"></i>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-800">Gestion des Rôles Utilisateurs</h2>
                </div>
                <div className="flex items-center space-x-2 text-sm text-slate-600">
                  <i className="bi bi-person-check text-green-500"></i>
                  <span>{filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
            <div className="p-6">
              {/* Barre de recherche */}
              <div className="mb-6">
                <div className="relative max-w-md">
                  <input
                    id="searchUsers"
                    type="text"
                    placeholder="Rechercher par nom ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-10 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 text-sm placeholder-slate-400"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="bi bi-search text-slate-400 text-lg"></i>
                  </div>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:scale-110 transition-transform"
                    >
                      <i className="bi bi-x-circle-fill text-slate-400 hover:text-slate-600 text-lg"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Contenu des utilisateurs */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200"></div>
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-indigo-600 absolute top-0 left-0"></div>
                  </div>
                  <p className="text-slate-500 mt-4 text-sm">Chargement des utilisateurs...</p>
                </div>
              ) : (
                <>
                  {/* Vue mobile - cartes */}
                  <div className="block sm:hidden space-y-4">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map(user => (
                        <div key={user._id} className="bg-gradient-to-r from-white to-gray-50 p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                          <div className="flex items-center mb-4">
                            <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mr-4 shadow-md">
                              {isExternalPhotoUrl(user.profilePhoto) ? (
                                <img src={user.profilePhoto} alt={user.name} className="h-10 w-10 rounded-xl object-cover" />
                              ) : (
                                <span className="text-white font-semibold text-sm">
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
                              <p className="text-xs text-slate-500 truncate">{user.email}</p>
                            </div>
                          </div>
                          
                          <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-1 gap-4">
                              <div className="bg-white p-3 rounded-lg border border-gray-100">
                                <span className="text-xs text-slate-500 block mb-2">Entreprise</span>
                                <select
                                  value={user.companyId || ""}
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      handleCompanyChangeRequest(user._id, e.target.value);
                                    }
                                  }}
                                  className="w-full text-sm py-2 px-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl focus:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all duration-200"
                                >
                                  <option value="">Aucune entreprise</option>
                                  {companies.map(company => (
                                    <option key={company._id} value={company._id}>
                                      {company.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white p-3 rounded-lg border border-gray-100">
                                  <span className="text-xs text-slate-500 block mb-2">Tester</span>
                                  <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={user.tester || false}
                                      onChange={(e) => handleTesterChange(user._id, e.target.checked)}
                                      className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                  </label>
                                </div>
                                <div className="bg-white p-3 rounded-lg border border-gray-100">
                                  <span className="text-xs text-slate-500 block mb-1">Statut</span>
                                  <div className="flex items-center space-x-1">
                                    <i className={`bi ${user.isAccountVerified ? 'bi-check-circle-fill text-green-500' : 'bi-exclamation-circle-fill text-amber-500'} text-sm`}></i>
                                    <span className={`text-xs font-medium ${user.isAccountVerified ? 'text-green-700' : 'text-amber-700'}`}>
                                      {user.isAccountVerified ? "Vérifié" : "Non vérifié"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex justify-between items-center bg-white p-3 rounded-lg border border-gray-100">
                              <div>
                                <span className="text-xs text-slate-500 block mb-1">Rôle actuel</span>
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                                  ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 
                                    user.role === 'manager' ? 'bg-blue-100 text-blue-800' : 
                                    'bg-green-100 text-green-800'}`}>
                                  <i className={`bi ${
                                    user.role === 'admin' ? 'bi-shield-fill' : 
                                    user.role === 'manager' ? 'bi-person-gear' : 
                                    'bi-person'
                                  } mr-1`}></i>
                                  {user.role}
                                </span>
                              </div>
                            </div>
                            
                            <div>
                              <select
                                value=""
                                onChange={(e) => {
                                  if (e.target.value) {
                                    handleRoleChange(user._id, e.target.value);
                                    e.target.value = "";
                                  }
                                }}
                                className="w-full text-sm py-2.5 px-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 transition-all duration-200"
                              >
                                <option value="" disabled>✨ Changer le rôle</option>
                                <option value="admin">🔒 Admin</option>
                                <option value="manager">⚙️ Manager</option>
                                <option value="user">👤 User</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="bi bi-person-x text-2xl text-gray-400"></i>
                        </div>
                        <p className="text-slate-500 font-medium">
                          {searchTerm ? "Aucun utilisateur trouvé" : "Aucun utilisateur disponible"}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {searchTerm ? "Essayez avec d'autres termes de recherche" : "Les utilisateurs apparaîtront ici une fois ajoutés"}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Vue desktop - tableau */}
                  <div className="hidden sm:block overflow-hidden rounded-xl border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-slate-50 to-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            <div className="flex items-center space-x-2">
                              <i className="bi bi-person text-slate-400"></i>
                              <span>Utilisateur</span>
                            </div>
                          </th>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            <div className="flex items-center space-x-2">
                              <i className="bi bi-envelope text-slate-400"></i>
                              <span>Email</span>
                            </div>
                          </th>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            <div className="flex items-center space-x-2">
                              <i className="bi bi-building text-slate-400"></i>
                              <span>Entreprise</span>
                            </div>
                          </th>
                          <th scope="col" className="px-6 py-4 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            <div className="flex items-center justify-center space-x-2">
                              <i className="bi bi-flask text-slate-400"></i>
                              <span>Tester</span>
                            </div>
                          </th>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            <div className="flex items-center space-x-2">
                              <i className="bi bi-shield text-slate-400"></i>
                              <span>Rôle</span>
                            </div>
                          </th>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            <div className="flex items-center space-x-2">
                              <i className="bi bi-arrow-repeat text-slate-400"></i>
                              <span>Actions</span>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map((user, index) => (
                            <tr key={user._id} className={`hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                              <td className="px-6 py-5 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-11 w-11 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                                    {isExternalPhotoUrl(user.profilePhoto) ? (
                                      <img src={user.profilePhoto} alt={user.name} className="h-11 w-11 rounded-xl object-cover" />
                                    ) : (
                                      <span className="text-white font-bold text-sm">
                                        {user.name.charAt(0).toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-semibold text-slate-900">{user.name}</div>
                                    <div className="flex items-center space-x-1 text-xs">
                                      <i className={`bi ${user.isAccountVerified ? 'bi-check-circle-fill text-green-500' : 'bi-exclamation-circle-fill text-amber-500'}`}></i>
                                      <span className={user.isAccountVerified ? 'text-green-600' : 'text-amber-600'}>
                                        {user.isAccountVerified ? "Vérifié" : "Non vérifié"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap">
                                <div className="text-sm text-slate-700 font-medium truncate max-w-xs">{user.email}</div>
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap">
                                <select
                                  value={user.companyId || ""}
                                  onChange={(e) => {
                                    if (e.target.value !== user.companyId) {
                                      handleCompanyChangeRequest(user._id, e.target.value);
                                    }
                                  }}
                                  className="block w-48 text-sm py-2 px-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl focus:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all duration-200 shadow-sm hover:shadow-md"
                                >
                                  <option value="">🏢 Aucune entreprise</option>
                                  {companies.map(company => (
                                    <option key={company._id} value={company._id}>
                                      {company.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap">
                                <div className="flex justify-center">
                                  <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={user.tester || false}
                                      onChange={(e) => handleTesterChange(user._id, e.target.checked)}
                                      className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                  </label>
                                </div>
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap">
                                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm
                                  ${user.role === 'admin' ? 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-200' : 
                                    user.role === 'manager' ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-200' : 
                                    'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-200'}`}>
                                  <i className={`bi ${
                                    user.role === 'admin' ? 'bi-shield-fill' : 
                                    user.role === 'manager' ? 'bi-person-gear' : 
                                    'bi-person'
                                  } mr-1.5`}></i>
                                  {user.role}
                                </span>
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap">
                                <select
                                  value=""
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      handleRoleChange(user._id, e.target.value);
                                      e.target.value = "";
                                    }
                                  }}
                                  className="block w-36 text-sm py-2 px-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 shadow-sm hover:shadow-md"
                                >
                                  <option value="" disabled>✨ Modifier</option>
                                  <option value="admin">🔒 Admin</option>
                                  <option value="manager">⚙️ Manager</option>
                                  <option value="user">👤 User</option>
                                </select>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="px-6 py-16 text-center">
                              <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="bi bi-person-x text-2xl text-gray-400"></i>
                              </div>
                              <p className="text-slate-500 font-medium">
                                {searchTerm ? "Aucun utilisateur trouvé" : "Aucun utilisateur disponible"}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {searchTerm ? "Essayez avec d'autres termes de recherche" : "Les utilisateurs apparaîtront ici une fois ajoutés"}
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <i className="bi bi-star text-white text-sm"></i>
                </div>
                <h2 className="text-lg font-semibold text-slate-800">Gestion des Avis</h2>
              </div>
            </div>
            <div className="p-6">
              <AvisManagement
                showDeleteDialog={showDeleteDialog}
                setShowDeleteDialog={setShowDeleteDialog}
                avisToDelete={avisToDelete}
                setAvisToDelete={setAvisToDelete}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog - Suppression d'avis */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setAvisToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Supprimer l'avis"
        message="Êtes-vous sûr de vouloir supprimer cet avis ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />

      {/* Confirmation Dialog - Changement d'entreprise */}
      <ConfirmationDialog
        isOpen={showCompanyChangeDialog}
        onClose={() => {
          setShowCompanyChangeDialog(false);
          setPendingCompanyChange(null);
        }}
        onConfirm={confirmCompanyChange}
        title="Modifier l'entreprise"
        message={
          pendingCompanyChange
            ? `Êtes-vous sûr de vouloir assigner ${pendingCompanyChange.userName} à l'entreprise "${pendingCompanyChange.companyName}" ?`
            : "Êtes-vous sûr de vouloir modifier l'entreprise de cet utilisateur ?"
        }
        confirmText="Confirmer"
        cancelText="Annuler"
        isDangerous={false}
      />
    </div>
  );
};

export default AdminTab;